import { useFocusedNavigation } from "../../../hooks/useFocusedNavigation";
import { CheckoutProvider } from "./lib/CheckoutProvider";
import { RyftCardPaymentProvider } from "./lib/RyftCardPaymentProvider";
import { useCheckout } from "./lib/CheckoutProvider.shared";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import { EmptyBusinessBoundary } from "./lib/EmptyBusinessBoundary";
import { EmptySupplierBoundary } from "./lib/EmptySupplierBoundary";
import { EmptySupplierQueryParamBoundary } from "./lib/EmptySupplierQueryParamBoundary";
import { CheckoutPageLoading } from "./lib/CheckoutPageLoading";
import { ErrorMessage } from "../../../components/errorMessage/ErrorMessage";
import { CheckoutPageLayout } from "./lib/CheckoutPageLayout";

import { useEffect, useMemo } from "react";
import {
  BusinessGetResponseType,
  OrderCreateRetailerMetadataType,
  OrderV2Dto,
} from "@sellar-io/api-v2/types";
import { useBusinessUpdate } from "../../../hooks/api/v2/business/useBusinessUpdate";
import { useOrderCreate } from "../../../hooks/api/v2/order/v2/useOrderCreate";
import { useMediaQuery } from "usehooks-ts";
import { ORDER_BUYER } from "../../../constants/routes";
import { Mixpanel } from "../../../utils/mixpanel";
import {
  ContextOrderBasketType,
  useBasketContext,
  useEvents,
  useSellarUser,
} from "../../../hooks";

import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { CheckoutOrder, CheckoutOrderItem } from "./lib/types";
import { CheckoutOrderSummary } from "./lib/CheckoutOrderSummary";
import { mapToInitialOrder } from "./lib/mapToInitialOrder";
import { mapToLegacyPaymentType } from "./lib/mapToLegacyPaymentType";
import { Checkout } from "./lib/Checkout";
import { useCheckoutOrderSummary } from "./lib/useCheckoutOrderSummary";
import { isHttpError } from "~/utils/httpClient";
import {
  CheckoutAlerts,
  ErrorMessageAlert,
  PricingChangeAlert,
  DistributionEducationAlert,
  StockLevelAlert,
} from "./lib/CheckoutAlerts";
import { mapBasketOrderToCheckoutOrderItems } from "./lib/mapBasketOrderToCheckoutOrderItems";
import { useCheckoutPaymentOptions } from "../../../hooks/api/v2/checkout/useCheckoutPaymentOptions";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useUser } from "../../../hooks/api/v2/user/useUser";
import { useValidateCheckout } from "./lib/useValidateCheckout";
import { ProcessOrderReturnType, usePlaceOrder } from "./lib/usePlaceOrder";
import { useStorefrontPromotionRewardItemSelectorParameters } from "../categoryPage/Storefront/StorefrontPromotion/StorefrontPromotionRewardItemSelector";
import { useLegalEntityCompanyCreditInfo } from "../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { useStorefrontBuyerChoiceDeliveryOption } from "../categoryPage/Storefront/StorefrontBuyerChoiceDeliveryOptionDialog/useStorefrontBuyerChoiceDeliveryOptions";

export const CheckoutV2CreateOrderPage = () => {
  useFocusedNavigation(); // Hides all drawers - sidebar and basket drawer
  return (
    <CheckoutProvider>
      <EmptySupplierQueryParamBoundary>
        <EmptyBusinessBoundary>
          <EmptySupplierBoundary>
            <RyftCardPaymentProvider>
              <CheckoutV2CreateOrderLayout />
            </RyftCardPaymentProvider>
          </EmptySupplierBoundary>
        </EmptyBusinessBoundary>
      </EmptySupplierQueryParamBoundary>
    </CheckoutProvider>
  );
};

const CheckoutV2CreateOrderLayout = () => {
  const [{ temporaryCartId, order: initialBasketOrder }] = useBasketContext();
  const checkoutOrderItems = useMemo(() => {
    const basketOrderItems = initialBasketOrder?.OrderItems ?? [];
    return mapBasketOrderToCheckoutOrderItems(basketOrderItems);
  }, [initialBasketOrder?.OrderItems]);

  const { sessionId } = useMarketStorefrontSessionId();
  const storefrontPromotionRewardItemSelectorParameters =
    useStorefrontPromotionRewardItemSelectorParameters();
  const {
    isLoading: isCheckoutLoading,
    business,
    supplier,
    error: checkoutPageError,
    connection,
    priceTierLink,
    supplierUrlIdInParam,
    storefront,
    storefrontFulfillmentType,
    checkoutFulfillmentType,
    paymentMethods,
    updateFulfillmentType,
    deleteDraftOrder,
  } = useCheckout();
  if (!business) throw new Error("Business object is missing"); // We're sure business exists because of <EmptyBusinessBoundary />
  if (!supplier) throw new Error("Supplier object is missing"); // We're sure supplier exists because of <EmptySupplierBoundary />

  const isMobile = useMediaQuery("(max-width: 768px)");
  const isLargeScreen = useMediaQuery("(min-width: 1900px)");

  const businessUpdateMutation = useBusinessUpdate(business.id);
  const orderCreateMutation = useOrderCreate();
  const { user } = useSellarUser();
  const { user: firebaseUser } = useFirebaseUser();
  const userQuery = useUser(user?.id ?? 0);
  const { updateUserProfile } = useEvents();
  const canRequestCreditTerms = useFeatureFlagStore().isActive(
    "CHECKOUT_CREDIT_TERMS_REQUEST",
  );
  const {
    saveBuyersChoiceFulfillmentType,
    isValidBuyersChoiceFulfillmentType,
  } = useStorefrontBuyerChoiceDeliveryOption({ supplierId: supplier.id });

  const userId = user?.id ?? 0;

  const {
    loading: isPlacingOrder,
    error: placeOrderErrorMessage,
    mode: placeOrderMode,
    placeOrder,
    setError: setPlaceOrderErrorMessage,
  } = usePlaceOrder();
  const {
    handleValidateCheckout,
    loading: isValidatingCart,
    outOfStockProducts,
    pricingChangeProducts,
    activeProducts,
  } = useValidateCheckout();

  const supplierName = supplier.Business.name;
  const retailerId = business.Retailer?.id ?? 0;
  const supplierId = supplier.id;
  const isPageLoading = isCheckoutLoading || !firebaseUser;
  const navTitle = `Checkout with ${supplierName}`;
  const supplierRelationshipPaymentDue =
    connection?.PaymentTermsRequest?.paymentDue ?? 0;
  const supplierRelationshipPaymentDueCalculateFrom =
    connection?.PaymentTermsRequest?.paymentDueCalculateFrom ??
    "invoiceIssueDate";
  const supplierRelationshipStatus =
    connection?.PaymentTermsRequest?.status ?? "pending";

  const pricingChangeLookupMap = new Map(
    pricingChangeProducts.map((product) => [product.id, product.newPrice]),
  );

  const updatedBasketOrder: ContextOrderBasketType = {
    ...initialBasketOrder,
    RetailerId: initialBasketOrder?.RetailerId ?? null,
    OrderItems: initialBasketOrder?.OrderItems?.map((orderItem) => {
      const price = pricingChangeLookupMap.has(orderItem.Product.id)
        ? (pricingChangeLookupMap.get(orderItem.Product.id) ?? 0)
        : orderItem.price;
      return {
        ...orderItem,
        price,
      };
    }),
  };
  const updatedCheckoutOrderItems = mapBasketOrderToCheckoutOrderItems(
    updatedBasketOrder.OrderItems ?? [],
  );

  const orderSummary = useCheckoutOrderSummary({
    supplierStorefront: storefront,
    order: updatedBasketOrder,
  });

  const initialOrderFulfillmentType =
    checkoutFulfillmentType ??
    storefrontFulfillmentType ??
    connection?.fulfillmentType;

  const { data: paymentOptions, error: paymentOptionsError } =
    useCheckoutPaymentOptions(
      {
        supplierId: supplier.id,
        orderTotal: orderSummary.totalCharge,
        orderItems: updatedCheckoutOrderItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        orderFulfillmentType:
          checkoutFulfillmentType ?? initialOrderFulfillmentType,
        canRequestCreditTerms,
      },
      {
        enabled: Boolean(supplier.id) && Boolean(retailerId) && Boolean(userId),
      },
    );

  const paymentOptionsErrorMessage = paymentOptionsError
    ? "Error fetching payment options."
    : "";

  const availableCheckoutPaymentOptions = paymentOptions ?? [];

  const canDisplaySellarCreditAsPaymentOption = Boolean(
    availableCheckoutPaymentOptions.includes("sellarCredit"),
  );

  const sellarCreditBalanceQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: retailerId,
    },
    {
      enabled: !!retailerId && canDisplaySellarCreditAsPaymentOption,
    },
  );

  const canUserViewSellarCreditBalance =
    sellarCreditBalanceQuery.data?.userCanViewSellarCreditBalance ?? false;

  const sellarCreditBalance =
    sellarCreditBalanceQuery.data?.creditAmountAvailable ?? 0;

  const sellarCreditStatus =
    sellarCreditBalanceQuery.data?.status ?? "creditCheckNotPerformed";

  const isCompanyVerified =
    sellarCreditBalanceQuery.data?.isCompanyVerified ?? null;

  const creditProvider = sellarCreditBalanceQuery.data?.creditProvider ?? "two";

  const hasDraughtDutyOrderItems = updatedCheckoutOrderItems.some(
    (item) => item.qualifiesForDraughtDuty,
  );

  const handleCreateOrder = async (
    params: CreateNewOrderDataParams,
    options: { idempotencyKey?: string },
  ): Promise<ProcessOrderReturnType> => {
    const orderData = createNewOrderData(params);
    try {
      // Update Delivery instructions on the business
      await businessUpdateMutation.mutateAsync({
        deliveryInstructions: params.order.deliveryInstructions,
      });

      // Create the order
      // Avoid adding an onError/onSuccess method to this mutation
      // Both modes are explicitly handled by usePlaceOrder
      const { id } = await orderCreateMutation.mutateAsync({
        ...orderData,
        idempotencyKey: options.idempotencyKey,
      });

      // Post order creation steps

      // Delete order from draft immediately after creating the order
      // in the API
      deleteDraftOrder({
        initiator: orderData.sellarMetadata.initiator,
        SupplierId: params.supplierId,
        RetailerId: params.business.Retailer?.id ?? 0,
      });

      // Save buyers choice preferences
      if (isValidBuyersChoiceFulfillmentType(orderData.data.fulfillmentType)) {
        saveBuyersChoiceFulfillmentType(orderData.data.fulfillmentType);
      }

      // Update Mixpanel - TODO - Can we do this from the Backend?
      const trackingOrderTotal = Mixpanel.formatAmountForTracking(
        orderSummary.totalCharge,
      );

      Mixpanel.people.increment("Lifetime Order Volume", trackingOrderTotal);
      Mixpanel.people.increment("Orders Placed", 1);

      // Update last order placed date - Also Mixpanel
      updateUserProfile(user?.id, {
        lastOrderPlaced: new Date().toISOString(),
      });

      return { data: { id }, error: null };
    } catch (error) {
      const newError = isHttpError(error)
        ? error
        : new Error("Something went wrong");
      return {
        data: null,
        error: newError,
      };
    }
  };

  const goToSupplierStorefront = () => {
    window.location.replace(
      `${window.location.origin}${ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlIdInParam })}`,
    );
  };

  const handleProcessOrder = (
    order: CheckoutOrder,
    options: { idempotencyKey?: string },
  ) => {
    return handleCreateOrder(
      {
        order,
        priceTierLink,
        vatPercentage: Number(orderSummary.tax.percentage),
        orders: checkoutOrderItems,
        business,
        supplierId: supplier.id,
        metadata: {
          sessionId,
          isLargeScreen,
          isMobile,
          temporaryCartId,
        },
      },
      options,
    );
  };

  // Validate checkout when the page refreshes
  useEffect(() => {
    // ensure sufficient stock levels

    handleValidateCheckout({
      supplierId,
      orderItemList: checkoutOrderItems,
      business,
      user: firebaseUser,
      storefrontLinkPassword: priceTierLink,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutOrderItems]);

  if (isPageLoading) {
    return (
      <CheckoutPageLoading description="Loading your order" title={navTitle} />
    );
  }

  if (checkoutPageError) {
    return <ErrorMessage>{checkoutPageError.message}</ErrorMessage>;
  }

  const smsNumber =
    userQuery.data?.smsNumber || business.phoneNumber?.trim() || "";

  const initialOrder = mapToInitialOrder({
    order: {
      deliveryNotes: business.deliveryInstructions ?? "",
      DeliveryAddress: business.DeliveryAddress,
      InvoiceAddress: business.InvoiceAddress,
      purchaseOrderNumber: "",
      retailerContactSmsNumber: smsNumber,
      paymentTermsRequest: {
        paymentDue: supplierRelationshipPaymentDue,
        status: supplierRelationshipStatus,
        paymentDueCalculateFrom: supplierRelationshipPaymentDueCalculateFrom,
      },
      hasNoDraughtDutyRelief: false,
      price: orderSummary.totalCharge,
    },
    connection,
    fulfillmentType: checkoutFulfillmentType ?? initialOrderFulfillmentType,
  });

  return (
    <>
      <CheckoutPageLayout
        title={navTitle}
        onClickLeftIcon={goToSupplierStorefront}
        layout="centered"
        aside={
          <CheckoutOrderSummary
            orderSummary={orderSummary}
            orderItemList={updatedCheckoutOrderItems}
            outOfStockProductIds={outOfStockProducts.map(
              (outOfStockProduct) => outOfStockProduct.id,
            )}
            productIdsWithPricingChange={pricingChangeProducts.map(
              (pricingChangeProduct) => pricingChangeProduct.id,
            )}
            activeProductIdList={activeProducts.map(
              (activeProduct) => activeProduct.id,
            )}
            promotions={
              storefrontPromotionRewardItemSelectorParameters.promotions
            }
          />
        }
        alerts={
          <CheckoutAlerts
            alerts={[
              <ErrorMessageAlert
                key="error-message-alert"
                message={placeOrderErrorMessage || paymentOptionsErrorMessage}
              />,
              <StockLevelAlert
                key="out-of-stock-alert"
                outOfStockProducts={outOfStockProducts}
                supplierName={supplierName}
                onClick={goToSupplierStorefront}
              />,
              <PricingChangeAlert
                key="pricing-change-alert"
                pricingChangeProducts={pricingChangeProducts}
                oldCheckoutOrderItems={checkoutOrderItems}
                newCheckoutOrderItems={updatedCheckoutOrderItems}
              />,
              <DistributionEducationAlert
                key="sellar-distribution-education-alert"
                supplierName={supplierName}
                fulfillmentType={initialOrder.deliveryType}
              />,
            ]}
          />
        }
      >
        <Checkout
          key={availableCheckoutPaymentOptions.join(",")} //This ensures that we have an up to date list of available payment types at all times
          title={navTitle}
          orderSummary={orderSummary}
          basketOrder={updatedBasketOrder}
          canUserViewSellarCreditBalance={canUserViewSellarCreditBalance}
          isOrderValueGreaterThanCreditBalance={
            orderSummary.totalCharge > sellarCreditBalance
          }
          placeOrderMode={placeOrderMode}
          initialIdempotencyKey={initialBasketOrder?.idempotencyKey}
          sellarCreditStatus={sellarCreditStatus}
          isCompanyVerified={isCompanyVerified}
          creditProvider={creditProvider}
          isValidatingCart={isValidatingCart}
          hasDraughtDutyOrderItems={hasDraughtDutyOrderItems}
          availablePaymentTypes={availableCheckoutPaymentOptions}
          sellarCreditBalance={sellarCreditBalance}
          initialCheckoutOrder={initialOrder}
          business={business}
          supplier={supplier}
          hasOutOfStockItems={outOfStockProducts.length > 0}
          isPlacingOrder={isPlacingOrder}
          checkoutProviderProps={{
            connection,
            priceTierLink,
            checkoutFulfillmentType,
            updateFulfillmentType,
            storefront,
            paymentMethods,
          }}
          // called when changing collapsible checkout card

          onValidateCheckout={async () => {
            // ensure sufficient stock levels

            await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });
          }}
          onUpdateInvoiceEmails={async (emails: string[]) => {
            await businessUpdateMutation.mutateAsync({
              invoiceEmails: emails,
            });
          }}
          onPlaceOrderWithStripe={async ({
            order,
            stripe,
            elements,
            options,
          }) => {
            if (!elements || !stripe) {
              setPlaceOrderErrorMessage(
                "Something went wrong while trying to process this order",
              );
              return;
            }

            // ensure sufficient stock levels

            const validationResponse = await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });

            const isValid = validationResponse.outOfStockProducts.length === 0;

            if (!isValid) return;

            // card payment

            if (order.payment.type === "new-card-payment") {
              await placeOrder({
                type: "place-order-with-new-card",
                stripeElements: elements,
                stripePaymentMethodId: undefined,
                stripe,
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order, options),
              });
              return;
            }

            if (order.payment.type === "existing-card-payment") {
              await placeOrder({
                type: "place-order-with-existing-card",
                stripePaymentMethodId: order.payment.paymentMethodId ?? "",
                stripeElements: undefined,
                stripe,
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order, options),
              });
              return;
            }

            // card payment with deposit

            if (order.payment.type === "new-card-payment-with-deposit") {
              await placeOrder({
                type: "place-order-with-new-card-payment-with-deposit",
                stripeElements: elements,
                stripePaymentMethodId: undefined,
                stripe,
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order, options),
              });
              return;
            }

            if (order.payment.type === "existing-card-payment-with-deposit") {
              await placeOrder({
                type: "place-order-with-existing-card-payment-with-deposit",
                stripePaymentMethodId: order.payment.paymentMethodId ?? "",
                stripeElements: undefined,
                stripe,
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order, options),
              });
              return;
            }
          }}
          onPlaceOrderWithoutStripe={async ({ order, options }) => {
            // ensure sufficient stock levels

            const validationResponse = await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });

            const isValid = validationResponse.outOfStockProducts.length === 0;

            if (!isValid) return;

            if (
              order.payment.type === "request-proforma-invoice" ||
              order.payment.type === "credit-account" ||
              order.payment.type === "pay-on-delivery" ||
              order.payment.type === "request-credit-terms"
            ) {
              await placeOrder({
                type: "place-order-without-card",
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order, options),
              });
              return;
            }

            if (order.payment.type === "sellar-credit") {
              await placeOrder({
                type: "place-order-with-sellar-credit",
                processOrder: () => handleProcessOrder(order, options),
                supplierUrlIdInParam,
              });
              return;
            }
          }}
          onPlaceOrderWithRyft={async ({ order, options }) => {
            const validationResponse = await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });

            const isValid = validationResponse.outOfStockProducts.length === 0;

            if (!isValid) {
              return;
            }

            await placeOrder({
              type: "place-order-with-new-ryft-card",
              supplierUrlIdInParam,
              processOrder: () => handleProcessOrder(order, options),
            });
            return;
          }}
        />
      </CheckoutPageLayout>
      {isPlacingOrder && (
        <div className="absolute inset-0 z-50">
          <CheckoutPageLoading
            description="Placing your order"
            title={navTitle}
          />
        </div>
      )}
    </>
  );
};

type CreateNewOrderDataParams = {
  order: CheckoutOrder;
  priceTierLink: string | undefined;
  vatPercentage: number;
  orders: CheckoutOrderItem[];
  business: BusinessGetResponseType;
  supplierId: number;
  metadata: {
    sessionId: string | null;
    temporaryCartId: string;
    isMobile: boolean;
    isLargeScreen: boolean;
  };
};

const createNewOrderData = ({
  order,
  priceTierLink,
  orders,
  business,
  vatPercentage,
  supplierId,
  metadata,
}: CreateNewOrderDataParams): {
  data: OrderV2Dto.OrderCreateRequestType;
  sellarMetadata: OrderCreateRetailerMetadataType;
} => {
  if (!order.payment.type) throw new Error("Payment type not set");
  const storefrontLinkToken = priceTierLink;
  const paymentType = order.payment.type;
  const orderItems = orders.map((orderItem) => ({
    ProductId: Number(orderItem.productId),
    quantity: orderItem.quantity,
    PromotionRewardItemId: orderItem.promotionRewardItemId ?? undefined,
  }));
  const hasNoDraughtDutyRelief = order.draughtDuty === "yes";
  const legacyPaymentType = mapToLegacyPaymentType({
    paymentType,
    invoiceMethod: order.invoiceMethod,
  });
  const paymentTermsRequest =
    order.payment.type === "request-credit-terms"
      ? order.paymentTermsRequest
      : undefined;

  return {
    data: {
      storefrontLinkToken,
      checkoutSource: "storefront",
      initiator: "retailer",
      paymentType: legacyPaymentType,
      InvoiceAddressId: business.InvoiceAddressId,
      DeliveryAddressId: business.DeliveryAddressId,
      vatPercentage,
      DeliveryAddress: order.deliveryAddress,
      InvoiceAddress: order.invoiceAddress,
      currency: "gbp",
      SupplierId: supplierId,
      purchaseOrderNumber: order.purchaseOrderNumber,
      price: order.price,
      fulfillmentType: order.deliveryType,
      OrderItems: orderItems,
      orderComment: order.comment,
      retailerContactSmsNumber: order.phoneNumber || null,
      mailingListPermission: order.mailingListPermission,
      hasNoDraughtDutyRelief,
      paymentTermsRequest,
      distributionContactId: order.distributionContactId,
      RetailerSellarDistributionMeta: {
        timePreference: order.timePreference ?? null,
      },
      paymentLinkRequested:
        legacyPaymentType === "manual" &&
        order.paymentLinkRequested &&
        order.price > 0,
    },
    sellarMetadata: {
      initiator: "retailer",
      sessionId: metadata.sessionId,
      priceTierLink: Boolean(storefrontLinkToken),
      layout: metadata.isMobile
        ? "cards-compact"
        : metadata.isLargeScreen
          ? "table"
          : "cards",
      paymentIndication: order.payment.type,
      temporaryCartId: metadata.temporaryCartId,
    },
  };
};
